import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Typography, Grid, CircularProgress, Box, MenuItem, Rating, Snackbar, Alert
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import Layout from "../components/layout/InvoiceLayout";
import config from "../config.js";
import { useSelector } from "react-redux";

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;
  const kycId = useSelector((state) => state.kyc.selectedCompanyId);

  const [formData, setFormData] = useState({
    vendorId: "", companyName: "", contactPerson: "", email: "", phone: "",
    address: { city: "", state: "", postalCode: "" },
    servicesProvided: "", contractDetails: { startDate: "", endDate: "", paymentTerms: "" },
    ratings: 0, gstNumber: "", bankDetails: { bankName: "", accountNumber: "", ifscCode: "", upiId: "" },
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    if (kycId) {
      fetchVendors();
    } else {
      setLoading(false);
      setError("No company selected. Please select a company first.");
    }
  }, [kycId]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/getvenders/${kycId}`);
      

      
      // Handle potential data structures
      let vendorsArray = [];
      if (Array.isArray(response.data)) {
        vendorsArray = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // If response.data is an object with a data property containing vendors
        if (Array.isArray(response.data.data)) {
          vendorsArray = response.data.data;
        } else if (response.data.vendors && Array.isArray(response.data.vendors)) {
          vendorsArray = response.data.vendors;
        } else {
          // Last resort: try to extract values if it's an object with vendor objects
          vendorsArray = Object.values(response.data).filter(item => item && typeof item === 'object');
        }
      }
      
      setVendors(vendorsArray);
      setError(null);
    } catch (err) {
      console.error("API Error:", err);
      handleError("Failed to load vendors. Please try again later.");
      setVendors([]); // Ensure vendors is an empty array when there's an error
    } finally {
      setLoading(false);
    }
  };

  const generateVendorId = () => {
    const prefix = "V";
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substr(2, 5);
    return `${prefix}${timestamp}${randomStr}`.toUpperCase();
  };

  const handleClick = async () => {
    try {
      const data = { ...formData, kycId };
      if (selectedVendor) {
        await axios.put(`${API_BASE_URL}/api/v1/updateVenders/${selectedVendor._id}`, data);
        handleSuccess("Vendor updated successfully.");
      } else {
        await axios.post(`${API_BASE_URL}/api/v1/createVender/${kycId}`, data);
        handleSuccess("Vendor added successfully.");
      }
      fetchVendors();
      handleCloseDialog();
    } catch (err) {
      console.error("Error in handleClick:", err);
      handleError("Failed to save vendor. Please try again.");
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!vendorId) {
      handleError("Invalid vendor ID. Unable to delete.");
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/deleteVenders/${vendorId}`);
      handleSuccess("Vendor deleted successfully.");
      fetchVendors();
    } catch (err) {
      console.error("Error in handleDeleteVendor:", err);
      handleError("Failed to delete vendor. Please try again.");
    }
  }

  const handleOpenDialog = (vendor = null) => {
    if (vendor) {
      setFormData({
        ...vendor,
        // Ensure nested objects exist
        address: vendor.address || { city: "", state: "", postalCode: "" },
        contractDetails: vendor.contractDetails || { startDate: "", endDate: "", paymentTerms: "" },
        bankDetails: vendor.bankDetails || { bankName: "", accountNumber: "", ifscCode: "", upiId: "" }
      });
      setSelectedVendor(vendor);
    } else {
      resetForm();
      setFormData(prevData => ({
        ...prevData,
        vendorId: generateVendorId()
      }));
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVendor(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleNestedInputChange = (e, nestedField) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [nestedField]: { 
        ...prevData[nestedField] || {}, // Ensure nested object exists
        [name]: value 
      },
    }));
  };

  const resetForm = () => {
    setFormData({
      vendorId: "", companyName: "", contactPerson: "", email: "", phone: "",
      address: { city: "", state: "", postalCode: "" },
      servicesProvided: "", contractDetails: { startDate: "", endDate: "", paymentTerms: "" },
      ratings: 0, gstNumber: "", bankDetails: { bankName: "", accountNumber: "", ifscCode: "", upiId: "" },
    });
    setSelectedVendor(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSuccess = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleError = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Paper style={{ padding: 16, margin: 16 }}>
        <Typography variant="h5" gutterBottom>Vendor Management</Typography>
        {error && <Typography color="error" gutterBottom>{error}</Typography>}
        {kycId && (
          <Button
            variant="contained" startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()} style={{ marginBottom: 16, color: "white", background: "red" }}
          >
            Add New Vendor
          </Button>
        )}
        {!vendors || vendors.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" height="200px">
            <Typography variant="h6">{kycId ? "No vendors added yet" : "No company selected"}</Typography>
            <Typography>{kycId ? "Click 'Add New Vendor' to start managing your vendors" : "Please select a company"}</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor ID</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Services Provided</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(vendors) && vendors.map((vendor) => (
                  <TableRow key={vendor._id || vendor.vendorId}>
                    <TableCell>{vendor.vendorId}</TableCell>
                    <TableCell>{vendor.companyName}</TableCell>
                    <TableCell>{vendor.contactPerson}</TableCell>
                    <TableCell>{vendor.email}</TableCell>
                    <TableCell>{vendor.phone}</TableCell>
                    <TableCell>{vendor.servicesProvided}</TableCell>
                    <TableCell><Rating name="read-only" value={vendor.ratings || 0} readOnly /></TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenDialog(vendor)} size="small"><EditIcon /></IconButton>
                      <IconButton onClick={() => handleDeleteVendor(vendor._id)} size="small"><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedVendor ? "Edit Vendor" : "Add New Vendor"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Vendor ID"
                name="vendorId"
                value={formData.vendorId || ""}
                onChange={handleInputChange}
                required
                disabled={!!selectedVendor}
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={formData.companyName || ""}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Contact Person"
                name="contactPerson"
                value={formData.contactPerson || ""}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email || ""}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone || ""}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Services Provided"
                name="servicesProvided"
                value={formData.servicesProvided || ""}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.address?.city || ""}
                onChange={(e) => handleNestedInputChange(e, 'address')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="State"
                name="state"
                value={formData.address?.state || ""}
                onChange={(e) => handleNestedInputChange(e, 'address')}
                margin="normal"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Postal Code"
                name="postalCode"
                value={formData.address?.postalCode || ""}
                onChange={(e) => handleNestedInputChange(e, 'address')}
                margin="normal"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Contract Start Date"
                name="startDate"
                type="date"
                value={formData.contractDetails?.startDate || ""}
                onChange={(e) => handleNestedInputChange(e, 'contractDetails')}
                InputLabelProps={{ shrink: true }}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Contract End Date"
                name="endDate"
                type="date"
                value={formData.contractDetails?.endDate || ""}
                onChange={(e) => handleNestedInputChange(e, 'contractDetails')}
                InputLabelProps={{ shrink: true }}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                select
                label="Payment Terms"
                name="paymentTerms"
                value={formData.contractDetails?.paymentTerms || ""}
                onChange={(e) => handleNestedInputChange(e, 'contractDetails')}
                required
                margin="normal"
              >
                <MenuItem value="15 Days Net">15 Days Net</MenuItem>
                <MenuItem value="30 Days Net">30 Days Net</MenuItem>
                <MenuItem value="45 Days Net">45 Days Net</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="GST Number"
                name="gstNumber"
                value={formData.gstNumber || ""}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography component="legend" style={{ marginTop: 16 }}>Rating</Typography>
              <Rating
                name="ratings"
                value={formData.ratings || 0}
                onChange={(event, newValue) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    ratings: newValue,
                  }));
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bankName"
                value={formData.bankDetails?.bankName || ""}
                onChange={(e) => handleNestedInputChange(e, 'bankDetails')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Account Number"
                name="accountNumber"
                value={formData.bankDetails?.accountNumber || ""}
                onChange={(e) => handleNestedInputChange(e, 'bankDetails')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="IFSC Code"
                name="ifscCode"
                value={formData.bankDetails?.ifscCode || ""}
                onChange={(e) => handleNestedInputChange(e, 'bankDetails')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="UPI ID"
                name="upiId"
                value={formData.bankDetails?.upiId || ""}
                onChange={(e) => handleNestedInputChange(e, 'bankDetails')}
                required
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleClick} color="primary">
            {selectedVendor ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default VendorManagement;